/**
 * Barra flotante de la grabación de consulta (segunda pasada, 26/9/2026).
 *
 * La grabación ya no se para al cambiar de pestaña en la ficha ni al ir a
 * otra pantalla (vive en `gestorGrabacion`, lib/grabadoraConsulta.js). Para
 * que nadie se olvide de que se está grabando, mientras dura esta barra está
 * fija en TODAS las pantallas de la app (la monta AppShell):
 *
 *     ● Grabando 12:34 · Nombre Apellido · [Pausa] [Terminar]
 *
 * Al pulsarla se vuelve a la ficha del paciente, con su episodio a la vista.
 */
import React from 'react';
import { useNavigate } from 'react-router';
import { Pause, Play, Square, Loader2 } from 'lucide-react';
import { formatoTiempo, gestorGrabacion, useGrabacionEnCurso } from '../lib/grabadoraConsulta';

export default function BarraGrabacion() {
  const { sesion, estado } = useGrabacionEnCurso();
  const navigate = useNavigate();
  const fase = estado?.fase;
  if (!sesion || !['grabando', 'pausada', 'guardando'].includes(fase)) return null;

  const tiempo = formatoTiempo(estado.segundos);
  // (Fuera del JSX a propósito: «Guardando…» es un estado, no un botón de
  // guardar; botonesGuardar.test lee el texto de los botones.)
  let etiqueta = `Guardando ${tiempo}…`;
  if (fase === 'grabando') etiqueta = `Grabando ${tiempo}`;
  if (fase === 'pausada') etiqueta = `❚❚ En pausa ${tiempo}`;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="barra-grabacion"
      className="fixed z-50 bottom-4 left-1/2 -translate-x-1/2 lg:left-[calc(50%+140px)] max-w-[calc(100vw-2rem)]
        flex items-center gap-2 rounded-full border-2 border-red-400 bg-red-50 dark:bg-red-950 shadow-lg pl-4 pr-2 py-1.5"
    >
      <button
        type="button"
        onClick={() => gestorGrabacion.irA(navigate)}
        className="flex items-center gap-2 min-w-0 text-sm text-red-900 dark:text-red-100"
        title="Volver a la ficha del paciente"
        data-testid="barra-grabacion-ir"
      >
        <span className="font-semibold tabular-nums whitespace-nowrap" data-testid="barra-grabacion-tiempo">
          {fase === 'grabando' && <span className="animate-pulse text-red-600" aria-hidden>● </span>}
          {etiqueta}
        </span>
        {sesion.pacienteNombre && (
          <span className="truncate max-w-[14rem]">· {sesion.pacienteNombre}</span>
        )}
      </button>
      {fase === 'grabando' && (
        <button type="button" onClick={() => gestorGrabacion.pausar()} data-testid="barra-grabacion-pausa"
          className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-white dark:bg-card px-2.5 py-1 text-xs">
          <Pause className="w-3 h-3" /> Pausa
        </button>
      )}
      {fase === 'pausada' && (
        <button type="button" onClick={() => gestorGrabacion.seguir()} data-testid="barra-grabacion-seguir"
          className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-white dark:bg-card px-2.5 py-1 text-xs">
          <Play className="w-3 h-3" /> Seguir
        </button>
      )}
      {(fase === 'grabando' || fase === 'pausada') && (
        <button type="button" onClick={() => gestorGrabacion.terminar()} data-testid="barra-grabacion-terminar"
          className="inline-flex items-center gap-1 rounded-full bg-red-700 hover:bg-red-800 text-white px-2.5 py-1 text-xs">
          <Square className="w-3 h-3" /> Terminar
        </button>
      )}
      {fase === 'guardando' && <Loader2 className="w-4 h-4 animate-spin text-red-700" />}
    </div>
  );
}
